import { core } from "@tauri-apps/api";

export interface SwitchBotDevice {
  deviceId: string;
  deviceName: string;
  deviceType: string;
  hubDeviceId: string;
}

export interface SwitchBotDeviceStatus {
  battery?: number;
  deviceId: string;
  deviceType: string;
  hubDeviceId: string;
  humidity?: number;
  temperature?: number;
  version?: string;
}

/**
 * Speichert einen neuen SwitchBot-Token lokal
 *
 * @param token
 *
 * @returns {Promise<void>} Promise that resolves when token is saved
 *
 */
export async function saveSwitchBotToken(token: string): Promise<void> {
  try {
    await core.invoke("set_switchbot_token", { token });
  } catch (error) {
    // set_switchbot_token fehlgeschlagen
    throw new Error(`Token konnte nicht gespeichert werden: ${String(error)}`);
  }
}

/**
 * Speichert einen neuen SwitchBot-Secret-Key lokal
 *
 * @param secret
 *
 * @returns {Promise<void>} Promise that resolves when secret is saved
 *
 */
export async function saveSwitchBotSecret(secret: string): Promise<void> {
  try {
    await core.invoke("set_switchbot_secret", { secret });
  } catch (error) {
    // set_switchbot_secret fehlgeschlagen
    throw new Error(`Secret konnte nicht gespeichert werden: ${String(error)}`);
  }
}

/**
 * Lädt den aktuell gespeicherten SwitchBot-Token
 *
 * @returns {Promise<null | string>} Promise that resolves with token or null if not found
 *
 */
export async function loadSwitchBotToken(): Promise<null | string> {
  try {
    return await core.invoke<string>("get_switchbot_token");
  } catch (error) {
    // get_switchbot_token fehlgeschlagen
    throw new Error(`Token konnte nicht geladen werden: ${String(error)}`);
  }
}

/**
 * Lädt den aktuell gespeicherten SwitchBot-Secret-Key
 *
 * @returns {Promise<null | string>} Promise that resolves with secret or null if not found
 *
 */
export async function loadSwitchBotSecret(): Promise<null | string> {
  try {
    return await core.invoke<string>("get_switchbot_secret");
  } catch (error) {
    // get_switchbot_secret fehlgeschlagen
    throw new Error(`Secret konnte nicht geladen werden: ${String(error)}`);
  }
}

/**
 * Holt die SwitchBot-Geräte von der API (verwendet gespeicherte Credentials)
 *
 * @returns {Promise<SwitchBotDevice[]>} Promise that resolves with array of SwitchBot devices
 *
 */
export async function fetchSwitchBotDevices(): Promise<SwitchBotDevice[]> {
  try {
    const devices = await core.invoke<SwitchBotDevice[]>("fetch_switchbot_devices");
    return devices;
  } catch (error) {
    // fetch_switchbot_devices fehlgeschlagen
    throw new Error(`Geräte konnten nicht abgerufen werden: ${String(error)}`);
  }
}

/**
 * Holt den Status eines SwitchBot-Geräts (Temperatur, Luftfeuchtigkeit, Batterie)
 *
 * @param deviceId
 *
 * @returns {Promise<SwitchBotDeviceStatus>} Promise that resolves with device status
 *
 */
export async function fetchSwitchBotDeviceStatus(deviceId: string): Promise<SwitchBotDeviceStatus> {
  try {
    const status = await core.invoke<SwitchBotDeviceStatus>("fetch_switchbot_device_status", {
      deviceId,
    });
    return status;
  } catch (error) {
    // fetch_switchbot_device_status fehlgeschlagen
    throw new Error(`Geräte-Status konnte nicht abgerufen werden: ${String(error)}`);
  }
}
